import React from 'react';
import { Mail } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-6">
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-600">
              Centre for Sustainable AI
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto prose prose-lg">
            
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-gray-700 leading-relaxed mb-6">
                The Centre for Sustainable AI is committed to protecting the personal data of users of its websites. Our websites are an essential tool for advancing our mission to support safe, responsible, and sustainable artificial intelligence through research, policy, governance, and collaboration.
              </p>
              <p className="text-gray-700 leading-relaxed">
                This Privacy Policy describes the types of personal data we collect, how we use it, who has access to it and for how long, and the rights available to users under applicable data protection laws, including the UK GDPR and EU General Data Protection Regulation (GDPR). Certain activities supported by our websites may be subject to additional, specific data protection notices (for example, recruitment or event registration).
              </p>
            </div>

            {/* How We Collect */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">How We Collect and Use Personal Data</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect personal data through the use of our websites in two ways:
              </p>
              <ol className="list-decimal list-inside text-gray-700 mb-6 space-y-2">
                <li>Data you directly provide to us, and</li>
                <li>Data generated by your activity on our websites.</li>
              </ol>
            </div>

            {/* Data You Directly Provide */}
            <div className="mb-12">
              <h3 className="text-xl font-medium mb-4">Data You Directly Provide</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                You may choose to voluntarily provide personal data when interacting with our websites, including when:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Subscribing to newsletters or updates</li>
                <li>Registering for events, consultations, or briefings</li>
                <li>Submitting enquiries or expressions of interest</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                This data enables us to provide the services you request and may include:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Name</li>
                <li>Organisation and role</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Country or region</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                We only collect personal data that is necessary and proportionate to the purpose for which it is requested.
              </p>
            </div>

            {/* Data Generated by Your Activity */}
            <div className="mb-12">
              <h3 className="text-xl font-medium mb-4">Data Generated by Your Activity</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                To ensure the effective functioning, security, and continuous improvement of our websites, and to better understand visitor behaviour, we automatically collect certain information when you browse our websites, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Log information such as pages visited, date and time of visits, and IP address</li>
                <li>Device and software information such as browser type, browser version, and operating system</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                This information is collected through technologies such as cookies, which are small text files stored in your browser. Where user accounts or registrations apply, browsing activity may be linked to user credentials to support functionality and user experience.
              </p>
            </div>

            {/* Purpose of Processing */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Purpose of Processing</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We process personal data for the following purposes:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Providing requested services and communications</li>
                <li>Managing subscriptions, events, and engagement activities</li>
                <li>Improving website performance, usability, and security</li>
                <li>Supporting research, policy engagement, and outreach activities</li>
                <li>Ensuring compliance with legal and regulatory obligations</li>
              </ul>
              <p className="text-gray-700 leading-relaxed font-medium">
                We do not sell personal data or use it for commercial advertising purposes.
              </p>
            </div>

            {/* Access, Sharing, and Storage */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Access, Sharing, and Storage of Personal Data</h2>
              
              <h3 className="text-xl font-medium mb-4">Access Within the Organisation</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Access to personal data is restricted to authorised staff and associates of the Centre for Sustainable AI who require it to perform their professional duties.
              </p>

              <h3 className="text-xl font-medium mb-4">Use of Third-Party Service Providers</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                In some circumstances, personal data may be processed by trusted third-party service providers acting on our behalf, including for:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Website analytics</li>
                <li>Email communications such as newsletters, alerts, and event notifications</li>
                <li>Online surveys or consultations</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-6">
                These service providers process personal data under appropriate contractual and technical safeguards and in accordance with applicable data protection laws. Where data is transferred internationally, appropriate protections are applied.
              </p>

              <h3 className="text-xl font-medium mb-4">Data Retention</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Personal data provided directly by users is generally retained until:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>You unsubscribe or withdraw from the relevant service, or</li>
                <li>Two years after your last meaningful interaction with us</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                Data generated by website activity is retained for up to two years, after which it is deleted or anonymised.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Retention periods may vary where required by law or where specific notices apply.
              </p>
            </div>

            {/* Cookies */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Cookies and Similar Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our websites use cookies to support functionality, security, analytics, and user experience. You can control or disable cookies through your browser settings. Please note that disabling cookies may affect certain website features.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Where required, cookie consent mechanisms are implemented in accordance with applicable law.
              </p>
            </div>

            {/* Links to Other Websites */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Links to Other Websites</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our websites may contain links to websites operated by other organisations, including academic institutions, international organisations, social media platforms, and partner organisations.
              </p>
              <p className="text-gray-700 leading-relaxed">
                This Privacy Policy does not apply to third-party websites. The Centre for Sustainable AI is not responsible for the privacy practices or content of external websites, and users are encouraged to review the privacy notices of any third-party sites they visit.
              </p>
            </div>

            {/* Your Rights */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Your Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Under applicable data protection laws, you have the right to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Access your personal data</li>
                <li>Request correction of inaccurate or incomplete data</li>
                <li>Object to or restrict certain processing activities</li>
                <li>Request erasure of your personal data in certain circumstances</li>
                <li>Withdraw consent where processing is based on consent</li>
              </ul>
            </div>

            {/* Contact Us */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                For all enquiries related to this Privacy Policy, the processing of personal data, or the exercise of your data protection rights, please contact:
              </p>
              <div className="flex items-center gap-3 p-4 bg-white rounded-lg border mb-6">
                <Mail className="w-5 h-5 text-gray-600" />
                <a href="mailto:support@aiesg.org" className="text-black hover:underline">
                  support@aiesg.org
                </a>
              </div>
              <p className="text-gray-700 leading-relaxed">
                We will make reasonable efforts to respond to all legitimate requests in a timely manner and in accordance with applicable data protection laws.
              </p>
            </div>

            {/* Updates */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Updates to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                This Privacy Policy may be updated periodically to reflect changes in legal requirements, technology, or organisational activities. The most recent version will always be available on our website.
              </p>
            </div>

            {/* Last Updated */}
            <div className="pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Last updated: 28 January 2025
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
